// Follows "HR Design rules.md": GEN, DIS
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Toggle styles shared with ToggleGroupItem: button sizes (h-8 / h-7 / h-10), the button focus ring and the
 * motion tokens. `icon` is a 32×32 square with no side padding, so an icon-only item is centred.
 */
const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow] duration-fast ease-standard hover:bg-muted hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:text-disabled-foreground disabled:hover:bg-transparent data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-border bg-background hover:bg-accent hover:text-accent-foreground disabled:border-disabled-border disabled:bg-disabled',
      },
      size: {
        default: 'h-8 min-w-8 px-2',
        sm: 'h-7 min-w-7 px-1.5',
        lg: 'h-10 min-w-10 px-2.5',
        icon: 'size-8 min-w-8 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ToggleProps = Omit<React.ComponentProps<'button'>, 'onChange'> &
  VariantProps<typeof toggleVariants> & {
    pressed?: boolean;
    defaultPressed?: boolean;
    onPressedChange?: (pressed: boolean) => void;
  };

/** A two-state button (`aria-pressed`). Controlled with `pressed` or uncontrolled with `defaultPressed`. */
function Toggle({
  className,
  variant,
  size,
  pressed,
  defaultPressed = false,
  onPressedChange,
  onClick,
  ...props
}: ToggleProps) {
  const [ownPressed, setOwnPressed] = React.useState(defaultPressed);
  const isOn = pressed ?? ownPressed;
  return (
    <button
      type="button"
      data-slot="toggle"
      aria-pressed={isOn}
      data-state={isOn ? 'on' : 'off'}
      className={cn(toggleVariants({ variant, size, className }))}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (pressed === undefined) setOwnPressed(!isOn);
        onPressedChange?.(!isOn);
      }}
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
